import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ListChecks, Plus, CheckCircle, Clock, XCircle, AlertTriangle,
  CalendarDays, FileText, CreditCard, Star, ClipboardList, Bell,
  ExternalLink, BellOff, RotateCcw, Trash2, Building2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

const TYPE_CONFIG = {
  leave: { icon: CalendarDays, label: "Leave Approval", color: "bg-blue-50 text-blue-700 border-blue-200" },
  advance_loan: { icon: CreditCard, label: "Advance / Loan", color: "bg-purple-50 text-purple-700 border-purple-200" },
  performance_review: { icon: Star, label: "Performance Review", color: "bg-amber-50 text-amber-700 border-amber-200" },
  onboarding: { icon: ClipboardList, label: "Onboarding", color: "bg-orange-50 text-orange-700 border-orange-200" },
  document: { icon: FileText, label: "Documents", color: "bg-teal-50 text-teal-700 border-teal-200" },
  custom: { icon: Bell, label: "Reminder", color: "bg-slate-50 text-slate-700 border-slate-200" },
};

const PRIORITY_BADGE = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-500 border-slate-200",
};

const HRTasks = () => {
  const { currentBusiness } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [showDone, setShowDone] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", priority: "medium", due_date: "" });

  useEffect(() => {
    if (currentBusiness?.id) fetchTasks();
  }, [currentBusiness?.id]);

  const fetchTasks = async () => {
    try {
      const res = await api.get(`/hr-tasks?business_id=${currentBusiness.id}`);
      setTasks(res.data.tasks || []);
    } catch { toast.error("Failed to load HR tasks"); }
    finally { setLoading(false); }
  };

  const handleAction = async (taskId, action, snoozeDays = 1) => {
    try {
      await api.put(`/hr-tasks/${taskId}/action`, {
        action, business_id: currentBusiness.id, snooze_days: snoozeDays,
      });
      toast.success(`Task ${action === "done" ? "completed" : action}`);
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error("Title is required"); return; }
    try {
      await api.post("/hr-reminders", { ...formData, business_id: currentBusiness.id });
      toast.success("Reminder created");
      setDialogOpen(false);
      setFormData({ title: "", description: "", priority: "medium", due_date: "" });
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create reminder"); }
  };

  const handleDelete = async (reminderId) => {
    try {
      await api.delete(`/hr-reminders/${reminderId}`);
      toast.success("Reminder deleted");
      fetchTasks();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = tasks.filter(t => {
    if (filterType !== "all" && t.type !== filterType) return false;
    if (!showDone && t.status === "done") return false;
    return true;
  });

  const pendingCount = tasks.filter(t => t.status === "pending").length;
  const overdueCount = tasks.filter(t => t.overdue && t.status === "pending").length;
  const doneCount = tasks.filter(t => t.status === "done").length;

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to view HR tasks</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">HR Tasks & Reminders</h1>
          <p className="text-slate-500">All pending actions in one place — stay on top of everything</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-reminder-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Reminder
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Reminder</DialogTitle>
              <DialogDescription>Add a custom task or reminder</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateReminder} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Title</Label>
                <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Follow up on John's probation review" className="input-field" required
                  data-testid="reminder-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description (optional)</Label>
                <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional details..." className="input-field min-h-[60px]" data-testid="reminder-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Priority</Label>
                  <Select value={formData.priority} onValueChange={v => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger className="input-field" data-testid="reminder-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Due Date (optional)</Label>
                  <Input type="date" value={formData.due_date}
                    onChange={e => setFormData({ ...formData, due_date: e.target.value })}
                    className="input-field" data-testid="reminder-due-input" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-reminder-btn">Create Reminder</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-primary/10"><ListChecks className="h-6 w-6 text-primary" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{tasks.length}</p><p className="text-sm text-slate-500">Total Tasks</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-amber-100"><Clock className="h-6 w-6 text-amber-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{pendingCount}</p><p className="text-sm text-slate-500">Pending</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-red-100"><AlertTriangle className="h-6 w-6 text-red-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{overdueCount}</p><p className="text-sm text-slate-500">Overdue</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-green-100"><CheckCircle className="h-6 w-6 text-green-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{doneCount}</p><p className="text-sm text-slate-500">Completed</p></div></div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="input-field w-48" data-testid="filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="leave">Leave Approvals</SelectItem>
            <SelectItem value="advance_loan">Advances & Loans</SelectItem>
            <SelectItem value="performance_review">Performance Reviews</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="custom">Custom Reminders</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={showDone ? "default" : "outline"} size="sm" className="rounded-full text-xs"
          onClick={() => setShowDone(!showDone)} data-testid="toggle-done-btn">
          {showDone ? "Hide Completed" : "Show Completed"}
        </Button>
      </div>

      {/* Task List */}
      <Card className="card-soft" data-testid="task-list">
        <CardHeader><CardTitle className="font-heading text-lg">Tasks ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-300" />
              <p className="font-medium text-slate-600">All caught up!</p>
              <p className="text-sm">No pending tasks right now.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(task => {
                const cfg = TYPE_CONFIG[task.type] || TYPE_CONFIG.custom;
                const Icon = cfg.icon;
                const isDone = task.status === "done";
                const isSnoozed = task.status === "snoozed";
                return (
                  <div key={task.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      isDone ? "bg-green-50/30 border-green-100 opacity-60" :
                      task.overdue ? "bg-red-50/50 border-red-200" :
                      isSnoozed ? "bg-yellow-50/30 border-yellow-100 opacity-70" :
                      "bg-white border-slate-100 hover:shadow-sm"
                    }`}
                    data-testid={`task-${task.id}`}>
                    {/* Icon */}
                    <div className={`p-2 rounded-lg shrink-0 ${isDone ? "bg-green-100" : task.overdue ? "bg-red-100" : "bg-slate-50"}`}>
                      <Icon className={`h-4 w-4 ${isDone ? "text-green-600" : task.overdue ? "text-red-600" : "text-slate-500"}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`font-medium text-sm ${isDone ? "line-through text-slate-400" : "text-slate-700"}`}>{task.title}</p>
                        <Badge variant="outline" className={`${cfg.color} text-[10px]`}>{cfg.label}</Badge>
                        <Badge variant="outline" className={`${PRIORITY_BADGE[task.priority]} text-[10px]`}>{task.priority}</Badge>
                        {task.overdue && !isDone && (
                          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[10px] animate-pulse">
                            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Overdue
                          </Badge>
                        )}
                        {isSnoozed && <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-[10px]">Snoozed</Badge>}
                      </div>
                      {task.description && <p className="text-xs text-slate-400 mt-0.5">{task.description}</p>}
                      {task.due_date && <p className="text-[10px] text-slate-400 mt-0.5">Due: {task.due_date}</p>}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {task.link && !isDone && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-primary"
                          onClick={() => navigate(task.link)} title="Go to module" data-testid={`go-${task.id}`}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!isDone && (
                        <>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-green-600"
                            onClick={() => handleAction(task.id, "done")} title="Mark done" data-testid={`done-${task.id}`}>
                            <CheckCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-amber-600"
                            onClick={() => handleAction(task.id, "snoozed", 1)} title="Snooze 1 day" data-testid={`snooze-${task.id}`}>
                            <BellOff className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                            onClick={() => handleAction(task.id, "dismissed")} title="Dismiss" data-testid={`dismiss-${task.id}`}>
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {isDone && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-primary"
                          onClick={() => handleAction(task.id, "reopen")} title="Reopen" data-testid={`reopen-${task.id}`}>
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {task.type === "custom" && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                          onClick={() => handleDelete(task.reference_id)} title="Delete reminder" data-testid={`delete-${task.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HRTasks;
